/**
 * Trials analysis — ported from data-crunch/analyze_tests.py
 *
 * Two analyses:
 * 1. Parallel trials per month (stacked by group)
 * 2. Waiting time histogram (2024+): Date de demande → start of Période location
 */

function parseDate(s) {
    if (!s) return null;
    const trimmed = s.trim();
    if (!trimmed) return null;
    const parts = trimmed.split('-');
    if (parts.length !== 3) return null;
    const [y, m, d] = parts.map(Number);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    return new Date(y, m - 1, d);
}

function parsePeriod(s) {
    if (!s) return [null, null];
    const trimmed = s.trim();
    if (!trimmed) return [null, null];
    const parts = trimmed.split(' - ');
    if (parts.length === 2) return [parseDate(parts[0]), parseDate(parts[1])];
    if (parts.length === 1) { const d = parseDate(parts[0]); return [d, d]; }
    return [null, null];
}

function monthKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
}

function monthsCovered(start, end) {
    if (!start || !end) return [];
    const result = [];
    let y = start.getFullYear();
    let m = start.getMonth();
    const endY = end.getFullYear();
    const endM = end.getMonth();
    while (y < endY || (y === endY && m <= endM)) {
        result.push(`${y}-${String(m + 1).padStart(2, '0')}`);
        m++;
        if (m > 11) { m = 0; y++; }
    }
    return result;
}

function fillMonths(monthKeys) {
    if (monthKeys.length === 0) return [];
    const sorted = [...monthKeys].sort();
    const [minY, minM] = sorted[0].split('-').map(Number);
    const [maxY, maxM] = sorted[sorted.length - 1].split('-').map(Number);
    const result = [];
    let y = minY, m = minM;
    while (y < maxY || (y === maxY && m <= maxM)) {
        result.push(`${y}-${String(m).padStart(2, '0')}`);
        m++;
        if (m > 12) { m = 1; y++; }
    }
    return result;
}


const MIN_PARALLEL_MONTH = '2023-07';

const TYPE_PARTICULIER = 'Particulier';
const TYPE_PRO = 'Pro';
const TYPE_INCONNU = 'Inconnu';

const TYPE_COLORS = {
    [TYPE_PARTICULIER]: 'rgba(99,102,241,.7)',
    [TYPE_PRO]: 'rgba(245,158,11,.7)',
    [TYPE_INCONNU]: 'rgba(107,114,128,.5)',
};

function buildContactSets(contactsRows, contactsProRows) {
    const particulierNames = new Set();
    for (const row of (contactsRows || [])) {
        const name = (row['Nom'] || '').trim().toLowerCase();
        if (name) particulierNames.add(name);
    }
    const proNames = new Set();
    for (const row of (contactsProRows || [])) {
        const name = (row['Nom'] || '').trim().toLowerCase();
        if (name && !particulierNames.has(name)) proNames.add(name);
    }
    return { particulierNames, proNames };
}

function classifyTest(name, particulierNames, proNames, clientTypeMap) {
    const key = name.toLowerCase();
    if (particulierNames.has(key)) return TYPE_PARTICULIER;
    if (proNames.has(key)) return TYPE_PRO;
    // Fall back to CLIENTS Type field for people already converted
    const clientType = clientTypeMap[key];
    if (clientType === 'Particulier') return TYPE_PARTICULIER;
    if (clientType === 'Pro') return TYPE_PRO;
    return TYPE_INCONNU;
}

const EXCLUDED_CLIENT_GROUPS = new Set(['Wyes Internal Affairs']);

function buildClientMap(clientsRows) {
    const map = {};
    for (const row of (clientsRows || [])) {
        if (EXCLUDED_CLIENT_GROUPS.has((row['Groupe'] || '').trim())) continue;
        const name = (row['Nom'] || '').trim().toLowerCase();
        if (!name) continue;
        const rawType = (row['Type'] || '').trim();
        map[name] = rawType === 'Particulier' ? 'Particulier' : 'Pro';
    }
    return map;
}

/**
 * Analyze parsed tests CSV rows.
 * Returns { parallel, waitingTime, duration, conversion } with all data needed for rendering.
 *
 * @param {Array} rows - parsed TESTS CSV rows
 * @param {Array} [contactsRows] - parsed CONTACTS CSV rows (for pro/personal classification)
 * @param {Array} [contactsProRows] - parsed CONTACTS_PRO CSV rows
 * @param {Array} [clientsRows] - parsed CLIENTS CSV rows (for conversion analysis)
 */
export function analyzeTests(rows, contactsRows, contactsProRows, clientsRows) {
    // --- Parallel trials ---

    const { particulierNames, proNames } = buildContactSets(contactsRows, contactsProRows);
    const clientTypeMap = buildClientMap(clientsRows);

    const tests = [];
    const skipped = [];

    for (const row of rows) {
        const name = (row['Nom'] || '').trim();
        const group = (row['Groupe'] || '').trim();
        const period = (row['Période location'] || '').trim();
        const [start, end] = parsePeriod(period);

        if (!start || !end) {
            skipped.push(name);
            continue;
        }
        const type = classifyTest(name, particulierNames, proNames, clientTypeMap);
        const durationDays = Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)));
        const durationWeeks = Math.floor(durationDays / 7);
        tests.push({ name, group, type, startMonth: monthKey(start), months: monthsCovered(start, end), durationWeeks });
    }

    // Count active (parallel) tests per month
    const activeMonthCounts = {};
    for (const { months } of tests) {
        for (const ym of months) {
            activeMonthCounts[ym] = (activeMonthCounts[ym] || 0) + 1;
        }
    }

    // Count tests started per month, broken down by type
    const TYPE_ORDER = [TYPE_PARTICULIER, TYPE_PRO, TYPE_INCONNU];
    const startedByType = {};
    for (const t of TYPE_ORDER) startedByType[t] = {};
    for (const { startMonth, type } of tests) {
        startedByType[type][startMonth] = (startedByType[type][startMonth] || 0) + 1;
    }

    const allMonthKeys = new Set([MIN_PARALLEL_MONTH, ...Object.keys(activeMonthCounts), ...Object.keys(
        tests.reduce((acc, t) => { acc[t.startMonth] = 1; return acc; }, {})
    )]);
    const allMonths = fillMonths([...allMonthKeys]).filter(m => m >= MIN_PARALLEL_MONTH);

    // Chart datasets: stacked bars for started (by type), line for active
    const barDatasets = TYPE_ORDER
        .filter(t => tests.some(test => test.type === t))
        .map(t => ({
            type: 'bar',
            label: t,
            data: allMonths.map(m => startedByType[t][m] || 0),
            backgroundColor: TYPE_COLORS[t],
            borderRadius: 4,
            stack: 'started',
            order: 2,
        }));

    const datasets = [
        ...barDatasets,
        {
            type: 'line',
            label: 'Essais actifs (parallèles)',
            data: allMonths.map(m => activeMonthCounts[m] || 0),
            borderColor: 'rgba(239,68,68,1)',
            backgroundColor: 'rgba(239,68,68,.15)',
            borderWidth: 2,
            pointRadius: 3,
            pointBackgroundColor: 'rgba(239,68,68,1)',
            fill: false,
            tension: 0.3,
            order: 1,
        },
    ];

    // KPIs
    const totalTests = tests.length;
    let peakMonth = '-';
    let peakCount = 0;
    if (allMonths.length > 0) {
        peakMonth = allMonths.reduce((best, m) => (activeMonthCounts[m] || 0) > (activeMonthCounts[best] || 0) ? m : best, allMonths[0]);
        peakCount = activeMonthCounts[peakMonth] || 0;
    }
    const avgParallel = allMonths.length > 0
        ? Math.round(Object.values(activeMonthCounts).reduce((a, b) => a + b, 0) / allMonths.length * 10) / 10
        : 0;

    // --- Waiting time (2024+) ---

    let waitNodemande = 0;
    let waitNoPeriod = 0;
    let waitNoBoth = 0;
    let waitTotalRows = 0;
    const waitWeeks = [];

    for (const row of rows) {
        const demande = parseDate((row['Date de demande'] || ''));
        const periodStr = (row['Période location'] || '').trim();
        const [periodStart] = parsePeriod(periodStr);

        const refDate = demande || periodStart;
        if (refDate && refDate.getFullYear() < 2024) continue;

        waitTotalRows++;

        if (!demande && !periodStart) {
            waitNoBoth++;
        } else if (!demande) {
            waitNodemande++;
        } else if (!periodStart) {
            waitNoPeriod++;
        } else {
            const days = Math.max(0, Math.floor((periodStart - demande) / (1000 * 60 * 60 * 24)));
            waitWeeks.push(Math.floor(days / 7));
        }
    }

    // Filter out waiting times longer than 6 months (24 weeks)
    const MAX_WAIT_WEEKS = 24;
    const waitWeeksExcluded = waitWeeks.filter(w => w > MAX_WAIT_WEEKS).length;
    const waitWeeksFiltered = waitWeeks.filter(w => w <= MAX_WAIT_WEEKS);

    waitWeeksFiltered.sort((a, b) => a - b);
    const waitTotal = waitWeeksFiltered.length;
    const waitMedian = waitTotal > 0 ? waitWeeksFiltered[Math.floor(waitTotal / 2)] : 0;
    const waitMean = waitTotal > 0
        ? Math.round(waitWeeksFiltered.reduce((a, b) => a + b, 0) / waitTotal * 10) / 10
        : 0;

    // Histogram
    const waitMax = waitWeeksFiltered.length > 0 ? waitWeeksFiltered[waitWeeksFiltered.length - 1] : 0;
    const waitDist = {};
    for (const w of waitWeeksFiltered) { waitDist[w] = (waitDist[w] || 0) + 1; }
    const waitLabels = Array.from({ length: waitMax + 1 }, (_, i) => String(i));
    const waitData = Array.from({ length: waitMax + 1 }, (_, i) => waitDist[i] || 0);

    // Skip summary
    const waitSkippedTotal = waitNodemande + waitNoPeriod + waitNoBoth;
    const skipParts = [];
    if (waitNodemande) skipParts.push(`${waitNodemande} sans date de demande`);
    if (waitNoPeriod) skipParts.push(`${waitNoPeriod} sans période de location`);
    if (waitNoBoth) skipParts.push(`${waitNoBoth} sans aucune date`);

    // --- Duration distribution (by type) ---

    const MAX_DURATION_WEEKS = 24;
    const durationByType = {};
    for (const t of TYPE_ORDER) durationByType[t] = {};
    let durationExcluded = 0;
    const durationWeeksAll = [];

    for (const { type, durationWeeks } of tests) {
        if (durationWeeks > MAX_DURATION_WEEKS) {
            durationExcluded++;
            continue;
        }
        durationByType[type][durationWeeks] = (durationByType[type][durationWeeks] || 0) + 1;
        durationWeeksAll.push(durationWeeks);
    }

    durationWeeksAll.sort((a, b) => a - b);
    const durTotal = durationWeeksAll.length;
    const durMedian = durTotal > 0 ? durationWeeksAll[Math.floor(durTotal / 2)] : 0;
    const durMean = durTotal > 0
        ? Math.round(durationWeeksAll.reduce((a, b) => a + b, 0) / durTotal * 10) / 10
        : 0;

    const durMax = durTotal > 0 ? durationWeeksAll[durationWeeksAll.length - 1] : 0;
    const durLabels = Array.from({ length: durMax + 1 }, (_, i) => String(i));
    const durDatasets = TYPE_ORDER
        .filter(t => tests.some(test => test.type === t))
        .map(t => ({
            label: t,
            data: Array.from({ length: durMax + 1 }, (_, i) => durationByType[t][i] || 0),
            backgroundColor: TYPE_COLORS[t],
            borderRadius: 4,
        }));

    // --- Conversion analysis: tests → clients ---

    // For each test, check if the person is now a client
    // type = Particulier/Pro/Inconnu (based on CONTACTS/CONTACTS_PRO)
    // Inconnu = not in contacts → likely already a client
    const convByType = {};
    for (const t of TYPE_ORDER) convByType[t] = { total: 0, converted: 0 };

    // Also track converted tests by CLIENTS.Type
    const convertedByClientType = { Particulier: 0, Pro: 0 };
    let convTotal = 0;
    let convConverted = 0;

    for (const { name, type } of tests) {
        convByType[type].total++;
        convTotal++;
        const clientType = clientTypeMap[name.toLowerCase()];
        if (clientType) {
            convByType[type].converted++;
            convConverted++;
            convertedByClientType[clientType]++;
        }
    }

    // Also check skipped tests (no period) for conversion
    for (const name of skipped) {
        const clientType = clientTypeMap[name.toLowerCase()];
        if (clientType) {
            convertedByClientType[clientType]++;
        }
    }

    return {
        parallel: {
            labels: allMonths,
            datasets,
            kpis: { totalTests, peakMonth, peakCount, avgParallel, skippedCount: skipped.length },
            skippedNames: skipped,
        },
        waitingTime: {
            labels: waitLabels,
            data: waitData,
            kpis: { waitTotal, waitMedian, waitMean },
            totalRows: waitTotalRows,
            skippedTotal: waitSkippedTotal,
            skipParts,
            excludedLongWait: waitWeeksExcluded,
        },
        duration: {
            labels: durLabels,
            datasets: durDatasets,
            kpis: { total: durTotal, median: durMedian, mean: durMean },
            excluded: durationExcluded,
        },
        conversion: {
            total: convTotal,
            converted: convConverted,
            rate: convTotal > 0 ? Math.round(convConverted / convTotal * 100) : 0,
            byType: convByType,
            convertedByClientType,
        },
    };
}
